import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
dayjs.extend(timezone);

const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

function getCampaignDateContext(value?: string | Date, workspaceTimezone?: string) {
  const referenceDate = value == null ? dayjs() : dayjs(value);
  return workspaceTimezone ? referenceDate.tz(workspaceTimezone) : referenceDate;
}

export function canonicalizeCampaignDate(value: string, workspaceTimezone?: string) {
  if (ISO_DATE_PATTERN.test(value)) {
    if (workspaceTimezone) {
      return dayjs.tz(`${value}T00:00:00`, workspaceTimezone).toISOString();
    }

    return `${value}T00:00:00.000Z`;
  }

  return dayjs(value).toISOString();
}

type CampaignPeriodDateErrors = {
  startDate?: 'required' | 'out-of-range';
  endDate?: 'required' | 'before-start';
};

export function getCampaignStartDateBounds(isoDateString?: string, workspaceTimezone?: string) {
  const referenceDate = getCampaignDateContext(isoDateString, workspaceTimezone);

  return {
    min: referenceDate.startOf('day').toDate(),
    max: referenceDate.add(30, 'day').endOf('day').toDate(),
  };
}

export function getNextCampaignEndDate(workspaceTimezone?: string) {
  const nextCalendarDate = getCampaignDateContext(undefined, workspaceTimezone)
    .add(1, 'day')
    .format('YYYY-MM-DD');

  return canonicalizeCampaignDate(nextCalendarDate, workspaceTimezone);
}

export function getCampaignEndDateMin(args: {
  campaignStartDate?: DateISOString;
  referenceDate?: DateISOString;
  workspaceTimezone?: string;
}) {
  const minFromToday = getCampaignDateContext(args.referenceDate, args.workspaceTimezone)
    .add(1, 'day')
    .startOf('day');

  if (!args.campaignStartDate) {
    return minFromToday.toDate();
  }

  const minFromStartDate = getCampaignDateContext(args.campaignStartDate, args.workspaceTimezone)
    .add(1, 'day')
    .startOf('day');

  return (minFromStartDate.isAfter(minFromToday, 'day') ? minFromStartDate : minFromToday).toDate();
}

export function getCampaignPeriodDateErrors(args: {
  campaignStartDate?: DateISOString;
  hasEndDate: boolean;
  campaignEndDate?: DateISOString;
  referenceDate?: DateISOString;
  workspaceTimezone?: string;
}): CampaignPeriodDateErrors {
  const { min, max } = getCampaignStartDateBounds(args.referenceDate, args.workspaceTimezone);
  const errors: CampaignPeriodDateErrors = {};
  const startDate = args.campaignStartDate
    ? getCampaignDateContext(args.campaignStartDate, args.workspaceTimezone)
    : undefined;
  const endDate = args.campaignEndDate
    ? getCampaignDateContext(args.campaignEndDate, args.workspaceTimezone)
    : undefined;
  const minimumDate = getCampaignDateContext(min, args.workspaceTimezone);
  const maximumDate = getCampaignDateContext(max, args.workspaceTimezone);
  const minimumEndDate = getCampaignDateContext(
    getCampaignEndDateMin({
      campaignStartDate: args.campaignStartDate,
      referenceDate: args.referenceDate,
      workspaceTimezone: args.workspaceTimezone,
    }),
    args.workspaceTimezone,
  );

  if (!args.campaignStartDate) {
    errors.startDate = 'required';
  } else if (startDate?.isBefore(minimumDate, 'day') || startDate?.isAfter(maximumDate, 'day')) {
    errors.startDate = 'out-of-range';
  }

  if (!args.hasEndDate) {
    return errors;
  }

  if (!args.campaignEndDate) {
    errors.endDate = 'required';
    return errors;
  }

  if (endDate?.isBefore(minimumEndDate, 'day')) {
    errors.endDate = 'before-start';
  }

  return errors;
}
